/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { css } from 'aphrodite';
import { isEmpty } from 'lodash';
import { ActionButton, Text } from '@blend/enterprise-components';

import { InstructionModal } from 'blb-components/AuthenticatedApp/DashboardPage/InstructionModal';
import { BlendBridge } from 'blb-types/blendBridge';
import { closingTypeToDisplay } from 'blb-constants/global';
import { DocumentsCard } from 'blb-components/AuthenticatedApp/DashboardPage/DocumentsCard';
import { cardWrapperStyles } from 'blb-components/AuthenticatedApp/DashboardPage/SectionCard';
import { DashboardSection } from 'blb-components/AuthenticatedApp/DashboardPage/DashboardSection';
import { getConfig } from 'blb-utils/config';
import { DocumentTitles } from 'blb-components/AuthenticatedApp/DashboardPage/DocumentsCard/constants';
import { SubjectContext } from 'blb-contexts/Subject';
import { eSignOverviewBanner } from './OverviewBanner';
import { TabType } from 'src/components/AuthenticatedApp/ProfilePage';
import { DefaultProfileSidebar } from 'blb-components/AuthenticatedApp/ProfilePage/DefaultSidebar';
import { DashboardSectionItem } from 'blb-components/AuthenticatedApp/DashboardPage/DashboardSectionItem';

export const DEFAULT_LANDING_PATH = '/pipeline';

export interface SupportContact {
  email: string;
  phone?: string;
}
export const getSupportContactInfoForPrincipal = (principalType: BlendBridge.PrincipalType): SupportContact => {
  const { supportEmail, supportPhone } = getConfig();
  return {
    email: supportEmail[principalType] || supportEmail.default,
    phone: supportPhone[principalType],
  };
};

/**
 * Display each document individually with file name as the title
 * @param documents array of documents uploaded after closing
 * @param removeDoc
 * @returns
 */
const renderDocuments = (
  documents: BlendBridge.API.Document[],
  removeDoc?: (id: string) => void,
): React.ReactNode[] | boolean =>
  !isEmpty(documents) &&
  documents.map(doc => (
    <div className={css(cardWrapperStyles.singleCardWrapper)} key={doc._id}>
      <DocumentsCard
        documents={[doc]}
        id={`it-AdditionalClosingDocument-${doc._id}`}
        isUploaded={true}
        removeDoc={removeDoc}
        title={doc.fileName}
      />
    </div>
  ));

export const getDefaultDocumentSectionConfigs = (
  loanDocuments: BlendBridge.API.Document[],
  removeDoc: (id: string) => void,
): DashboardSection.Props['sectionConfigs'] => {
  const docComponent = (
    <div className={css(cardWrapperStyles.cardGroup)}>
      {!isEmpty(loanDocuments) && renderDocuments(loanDocuments, removeDoc)}
    </div>
  );
  return [
    {
      content: isEmpty(loanDocuments) ? <Text>{`Nothing uploaded yet.`}</Text> : docComponent,
      title: `Uploaded Documents`,
    },
  ];
};

export const renderDefaultDashboardOverview = (
  dashboardSections: DashboardSectionItem.SectionConfig[],
  loan: BlendBridge.API.Loan,
  documentSectionConfigs?: DashboardSectionItem.SectionConfig[],
  setInstructionOpen?: (val: boolean) => void,
  areInstructionsOpen?: boolean,
): React.ReactElement => {
  return (
    <>
      {dashboardSections.length > 0 && (
        <DashboardSection
          headerActions={
            setInstructionOpen &&
            !!loan?.closing && (
              <>
                <ActionButton
                  icon="list"
                  label="Instructions"
                  onClick={() => setInstructionOpen(true)}
                  theme="secondary"
                />
                <InstructionModal
                  closing={loan.closing}
                  handleClose={() => setInstructionOpen(false)}
                  isOpen={!!areInstructionsOpen}
                />
              </>
            )
          }
          sectionConfigs={dashboardSections}
          sectionHeader="Overview"
        />
      )}
      {documentSectionConfigs && <DashboardSection sectionConfigs={documentSectionConfigs} sectionHeader="Documents" />}
    </>
  );
};

/**
 * Determines if the current Document download request is the proper
 * download request the interrupt modal should be associated with.
 * @param  {string} documentTitle The label of the Document
 * @return {boolean}              Boolean flag
 */
export const shouldDownloadModalInterrupt = (documentTitle: string): boolean =>
  documentTitle === DocumentTitles.LENDER_INK_SIGN_CLOSING_DOCUMENTS;

/**
 * determines if the Unsigned eSign UIs should be displayed.
 * Criteria for true includes:
 *  - Settlement Agent Persona
 *  - Closing Type is HYBRID
 *  - Sign Ahead Docs are unsigned (loan.areSignAheadDocsSigned)
 * @param  {SubjectContext} subject SubjectContext containing the Loan/Closing
 * @return {boolean}                Yes/No flag to indicate display or not
 * @function
 */
export const shouldShowUnsignedESignUIs = (subject?: SubjectContext): boolean => {
  if (subject === undefined || subject === null) {
    return false;
  }

  const loan = subject.loan;
  const closing = subject.closing;

  if (
    loan === null ||
    loan.areSignAheadDocsSigned === undefined ||
    loan.featureFlags === undefined ||
    closing === undefined
  ) {
    return false;
  }

  if (!loan.areSignAheadDocsSigned && closing.closingType === 'HYBRID') {
    return true;
  }

  return false;
};

/**
 * Creates a DisplayConfig to render an Alert Banner for Settlement Agents
 * to be informed of unsigned e-sign Sign Ahead documents.
 * @return {DashboardSection.SectionConfig} DisplayConfig to render the UI
 * @function
 */
export const getESignBannerConfig = (closing?: EClose.FullClosing): DashboardSectionItem.SectionConfig | null => {
  if (closing === undefined || closing.closingType !== 'HYBRID') {
    return null;
  }

  const displayConfig: DashboardSectionItem.SectionConfig = {
    content: eSignOverviewBanner(),
    hideTitle: true,
    title: 'E-Sign Banner',
  };

  return displayConfig;
};

export const getClosingTypeCopy = (closing: EClose.FullClosing): string => {
  return closingTypeToDisplay[closing.closingType];
};

export const getProfileTabs = (_me: BlendBridge.API.User): TabType[] => [];

export const getPipelinePollingInterval = (): number => 0;

// TODO: Remove this function once all persona's have a `getSidebar` function defined
export const renderDefaultProfileSidebar = (): React.ReactElement => {
  return <DefaultProfileSidebar />;
};

export const getDashboardBanner = (
  _me: BlendBridge.API.User,
  loan: BlendBridge.API.Loan,
): BlendBridge.IAlert | undefined => {
  if (loan.archived) {
    return {
      message: `This loan is archived.`,
      theme: 'warn',
    };
  }
  return;
};
